// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Cursor pagination drainer, shared by the alarm and finding (anomaly) lists.
//
// Both AEM list endpoints return the same envelope and are always ordered
// `updated_at DESC` with no sort parameter, so any other ordering — severity, in
// particular — has to be applied client-side across the WHOLE set. A caller that
// keeps only the first page can't sort correctly, and its count is a floor rather
// than a total. That is exactly the bug this exists to remove: the Alarms badge
// read "100" because `page_size` defaults to 100 upstream and `next_cursor` was
// being dropped.

/** The page envelope both AEM list endpoints return. */
export interface CursorPage<T> {
  context: { page_size: number; has_next: boolean; next_cursor?: string | null };
  data: T[];
}

export interface DrainResult<T> {
  rows: T[];
  /** True when `maxPages` stopped the drain before upstream ran out of pages. */
  truncated: boolean;
}

/**
 * Upstream's documented `page_size` ceiling (the BFF enforces `ge=1, le=1000`).
 * Draining at the ceiling means most sites finish in a single request.
 */
export const MAX_PAGE_SIZE = 1000;

/**
 * Safety stop, in pages. At {@link MAX_PAGE_SIZE} that is 100k rows — far past
 * any real site, so hitting it means something is wrong (a cursor that doesn't
 * advance, or a site whose alarm volume needs a server-side answer rather than a
 * client-side one).
 */
export const DEFAULT_MAX_PAGES = 100;

/**
 * Follow `next_cursor` until upstream runs out of pages, concatenating `data`.
 *
 * Stops early at `maxPages` and reports it via `truncated` *and* a console
 * warning — a silent cap is what made the original truncation invisible, so this
 * never quietly returns a partial set.
 */
export async function drainCursor<T>(
  fetchPage: (_cursor?: string) => Promise<CursorPage<T>>,
  {
    maxPages = DEFAULT_MAX_PAGES,
    label = 'drainCursor',
  }: { maxPages?: number; label?: string } = {}
): Promise<DrainResult<T>> {
  const rows: T[] = [];
  let cursor: string | undefined;
  let pages = 0;

  do {
    const page = await fetchPage(cursor);
    rows.push(...page.data);
    pages += 1;
    cursor = page.context.has_next ? (page.context.next_cursor ?? undefined) : undefined;

    if (cursor && pages >= maxPages) {
      console.warn(
        `[${label}] stopped after ${pages} pages (${rows.length} rows) with more available upstream; ` +
          'the returned set is incomplete.'
      );
      return { rows, truncated: true };
    }
  } while (cursor);

  return { rows, truncated: false };
}
